import { useEffect, useMemo, useRef, useState } from "react"
import { Button } from "~/react/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "~/react/ui/popover"
import { Icon } from "~/react/ui/icon"
import { Alert, AlertTitle } from "~/react/ui/alert"
import { Separator } from "~/react/ui/separator"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "~/react/ui/dropdown-menu"
import SignaturePreviewContent from "~/react/components/signature_preview_content"

const TrixEditorElement = "trix-editor" as unknown as React.ElementType

type Props = {
  id: string
  value: string
  onChange: (html: string) => void
  className?: string
  previewSample?: {
    firstName?: string
    lastName?: string
  } | null
  selectedSenders?: Record<string, boolean>
}

export default function SignatureEditor({ id, value, onChange, className, previewSample, selectedSenders }: Props) {
  const hiddenRef = useRef<HTMLInputElement | null>(null)
  const editorRef = useRef<HTMLElement | null>(null)
  const imageInputRef = useRef<HTMLInputElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  const hasContent = useMemo(() => {
    if (!value) return false
    const text = value
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .trim()
    return text.length > 0
  }, [value])

  const hasHtmlFormatting = useMemo(() => {
    if (!hasContent) return false
    const text = value.trim()
    if (!text) return false

    const withoutStructuralTags = text
      .replace(/<\/?div[^>]*>/gi, "")
      .replace(/<\/?p[^>]*>/gi, "")
      .replace(/<br\s*\/?>/gi, "")

    const formattingTags =
      /<(strong|b|em|i|a|img|span|u|font|style|link|figure|figcaption|ul|ol|li|blockquote|table|thead|tbody|tr|td|th)/i
    return formattingTags.test(withoutStructuralTags)
  }, [value, hasContent])

  const previewContent = useMemo(() => {
    if (!hasContent) return ""

    const normalize = (text?: string | null) => (text || "").trim()
    const sampleFirstName = normalize(previewSample?.firstName)
    const sampleLastName = normalize(previewSample?.lastName)
    const sampleFullName = [sampleFirstName, sampleLastName].filter(Boolean).join(" ").trim()

    let preview = value.replace(/<code>(\s*{{[\s\S]*?}}\s*)<\/code>/gi, "$1").replace(/<\/?code>/gi, "")
    const replacements: Array<{ key: string; value: string }> = [
      { key: "firstname", value: sampleFirstName },
      { key: "first_name", value: sampleFirstName },
      { key: "lastname", value: sampleLastName },
      { key: "last_name", value: sampleLastName },
      {
        key: "fullname",
        value: sampleFullName || sampleFirstName || sampleLastName,
      },
      {
        key: "full_name",
        value: sampleFullName || sampleFirstName || sampleLastName,
      },
      {
        key: "sender_name",
        value: sampleFullName || sampleFirstName || sampleLastName,
      },
    ]

    replacements.forEach(({ key, value: replacementValue }) => {
      if (!replacementValue) return
      const regex = new RegExp(`\\{\\{\\s*${key}(?::\\s*[“"']([^}]*?)[”"'])?\\s*\\}\\}`, "gi")
      preview = preview.replace(regex, (_match, fallbackValue: string | undefined) => {
        const fallback = (fallbackValue || "").trim()
        return replacementValue || fallback || ""
      })
    })

    // For cases where replacementValue is empty (e.g., no sample), still remove the tag by applying regex once more
    replacements.forEach(({ key, value: replacementValue }) => {
      if (replacementValue) return
      const regex = new RegExp(`\\{\\{\\s*${key}(?::\\s*[“"']([^}]*?)[”"'])?\\s*\\}\\}`, "gi")
      preview = preview.replace(regex, (_match, fallbackValue: string | undefined) => (fallbackValue || "").trim())
    })

    const emailRegex = /\{\{\s*(email|email_address)(?::\s*["']([^}]*?)["'])?\s*\}\}/gi
    preview = preview.replace(emailRegex, (_match, _key, fallbackValue: string | undefined) => {
      return (fallbackValue || "").trim() || "email@example.com"
    })

    return preview
  }, [value, previewSample, hasContent])

  useEffect(() => {
    if (hiddenRef.current) hiddenRef.current.value = value || ""
    const el = editorRef.current as any
    if (el && el.editor) {
      // Only update if the content actually differs to avoid cursor jumps
      const current = (hiddenRef.current?.value || "").trim()
      const html = value || ""
      if (current !== html) {
        el.editor.loadHTML(html)
      }
    }
  }, [value])

  useEffect(() => {
    const el = editorRef.current
    if (!el) return
    const handler = () => {
      // defer read to ensure the hidden input has the latest html
      setTimeout(() => {
        if (hiddenRef.current) onChange(hiddenRef.current.value)
      }, 0)
    }
    el.addEventListener("trix-change", handler as EventListener)
    return () => el.removeEventListener("trix-change", handler as EventListener)
  }, [onChange])

  useEffect(() => {
    const input = imageInputRef.current
    const editor = editorRef.current as any
    if (!input || !editor || !editor.editor) return

    const handler = (event: Event) => {
      const target = event.target as HTMLInputElement
      if (target.files && target.files.length > 0) {
        editor.editor.insertFile(target.files[0])
        // Clear value after insertion to allow selecting the same file again
        setTimeout(() => {
          target.value = ""
        }, 100)
      }
    }

    input.addEventListener("change", handler)
    return () => input.removeEventListener("change", handler)
  }, [])

  useEffect(() => {
    if (!hasContent && previewOpen) {
      setPreviewOpen(false)
    }
  }, [hasContent, previewOpen])

  const hasSelectedSenders = useMemo(() => {
    if (!selectedSenders) return true // If not provided, don't require it
    return Object.values(selectedSenders).some((selected) => selected === true)
  }, [selectedSenders])

  const showPreviewButton = hasContent && hasSelectedSenders

  const insertAttribute = (attributeKey: string) => {
    const editorElement = document.getElementById(`${id}-editor`) as any
    if (!editorElement?.editor) return

    const attributeString = `{{${attributeKey}:""}}`
    const container = containerRef.current
    if (container) {
      // Try to get Stimulus controller
      const application = (window as any).Stimulus
      if (application) {
        try {
          const controller = application.getControllerForElementAndIdentifier(container, "trix-editor")
          if (controller?.verifyThenInsertAttribute) {
            controller.verifyThenInsertAttribute(editorElement.editor, attributeString)
            return
          }
        } catch (e) {
          // Fallback if controller not available
        }
      }
    }
    // Fallback: directly insert if controller not available
    editorElement.editor.insertString(attributeString)
    const cursorPosition = editorElement.editor.getSelectedRange()[1]
    editorElement.editor.setSelectedRange([cursorPosition - attributeString.length, cursorPosition])
    editorElement.editor.activateAttribute("customAttr")
  }

  return (
    <div
      ref={containerRef}
      className={`smtp-imap-signature${className ? ` ${className}` : ""}`}
      data-controller="trix-editor"
      data-trix-editor-show-attributes-value="true"
    >
      {hasHtmlFormatting && (
        <Alert variant="info" size="small" className="smtp-imap-signature__alert">
          <AlertTitle>{i18n.t("js.email_accounts.accounts.formatted_signature_warning")}</AlertTitle>
        </Alert>
      )}
      <div id={`${id}-toolbar`}></div>
      {/* Hidden subject target to satisfy controller when using attributes */}
      <input type="text" className="d-none" data-trix-editor-target="subjectField" />
      <data data-trix-editor-target="bodyContent" value={value}></data>
      <input id={`${id}-input`} ref={hiddenRef} type="hidden" defaultValue={value} onChange={() => {}} />
      <TrixEditorElement
        id={`${id}-editor`}
        ref={editorRef}
        input={`${id}-input`}
        toolbar={`${id}-toolbar`}
        data-trix-editor-target="editor"
        onChange={() => {}}
      />
      <div className="smtp-imap-signature__toolbar">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="xs"
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                const trix = document.getElementById(`${id}-editor`) as HTMLElement | null
                trix?.focus()
              }}
            >
              <Icon icon="brackets-curly" size="sm" />
              {i18n.t("js.email_accounts.accounts.insert_attribute").replace("{} ", "")}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem
              closeOnClick={true}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault()
                e.stopPropagation()
                insertAttribute("first_name")
              }}
            >
              {i18n.t("js.email_accounts.accounts.first_name")}
            </DropdownMenuItem>
            <DropdownMenuItem
              closeOnClick={true}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault()
                e.stopPropagation()
                insertAttribute("last_name")
              }}
            >
              {i18n.t("js.email_accounts.accounts.last_name")}
            </DropdownMenuItem>
            <DropdownMenuItem
              closeOnClick={true}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault()
                e.stopPropagation()
                insertAttribute("email_address")
              }}
            >
              {i18n.t("js.email_accounts.accounts.email")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Separator orientation="vertical" className="mx-1" />
        <Button
          variant="ghost"
          size="xs"
          data-action="mousedown->trix-editor#toggleFormat"
          data-format="bold"
          type="button"
          aria-label="Bold"
          iconOnly
        >
          <Icon icon="bold" size="sm" />
        </Button>
        <Button
          variant="ghost"
          size="xs"
          data-action="mousedown->trix-editor#toggleFormat"
          data-format="italic"
          type="button"
          aria-label="Italic"
          iconOnly
        >
          <Icon icon="italic" size="sm" />
        </Button>
        <Button
          variant="ghost"
          size="xs"
          data-action="mousedown->trix-editor#createLinkAction"
          data-trix-editor-target="linkButton"
          type="button"
          aria-label="Insert link"
          iconOnly
        >
          <Icon icon="link" size="sm" />
        </Button>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          data-trix-editor-target="imageAttachmentInput"
        />
        <Button
          variant="ghost"
          size="xs"
          data-action="mousedown->trix-editor#addImageAttachment"
          type="button"
          aria-label="Insert image"
          iconOnly
        >
          <Icon icon="image" size="sm" />
        </Button>
        {showPreviewButton && (
          <div className="ml-auto">
            <Popover open={previewOpen} onOpenChange={setPreviewOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="xs"
                  type="button"
                  aria-label={i18n.t("js.email_accounts.accounts.preview_signature")}
                  onMouseEnter={() => setPreviewOpen(true)}
                  onMouseLeave={() => setPreviewOpen(false)}
                >
                  <Icon icon="eye" aria-hidden="true" size="sm" />
                  {i18n.t("js.email_accounts.accounts.preview")}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="center"
                side="left"
                className="signature-preview-popover"
                onMouseEnter={() => setPreviewOpen(true)}
                onMouseLeave={() => setPreviewOpen(false)}
              >
                <SignaturePreviewContent previewContent={previewContent} />
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>
    </div>
  )
}
